import { useState } from 'react';
import { XCircle, ChevronDown, ChevronUp, Mail, User, Calendar, Check } from 'lucide-react';

interface SystemError {
  id: string;
  created_at: string;
  recipient_email: string;
  email_type: string;
  subject: string;
  campaign_id?: string;
  user_id?: string;
  response_status_code?: number;
  error_message: string;
  request_payload?: any;
  response_body?: any;
  sendgrid_message_id?: string;
  user_email?: string;
  campaign_subject?: string;
  reviewed?: boolean;
  reviewed_at?: string;
  reviewed_by?: string;
}

interface RecentErrorsTableProps {
  errors: SystemError[];
  loading: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onMarkReviewed?: (errorId: string, reviewed: boolean) => Promise<void>;
}

export default function RecentErrorsTable({ errors, loading, onLoadMore, hasMore, onMarkReviewed }: RecentErrorsTableProps) {
  const [expandedError, setExpandedError] = useState<string | null>(null);
  const [updatingReviewed, setUpdatingReviewed] = useState<string | null>(null);

  const toggleExpanded = (errorId: string) => {
    setExpandedError(expandedError === errorId ? null : errorId);
  };

  const handleMarkReviewed = async (errorId: string, currentlyReviewed: boolean, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!onMarkReviewed) return;

    setUpdatingReviewed(errorId);
    try {
      await onMarkReviewed(errorId, !currentlyReviewed);
    } catch (error) {
      console.error('Failed to update reviewed status:', error);
    } finally {
      setUpdatingReviewed(null);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading && errors.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Fouten laden...</p>
      </div>
    );
  }

  if (!loading && errors.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Geen fouten gevonden</h3>
        <p className="text-gray-600">Er zijn geen verzendfouten geregistreerd in het systeem.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errors.map((error) => {
        const isExpanded = expandedError === error.id;

        return (
          <div
            key={error.id}
            className={`border rounded-lg overflow-hidden transition-all ${
              error.reviewed
                ? 'bg-gray-50 border-gray-300 opacity-75'
                : 'bg-white border-gray-200'
            }`}
          >
            <div
              className="p-4 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => toggleExpanded(error.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <button
                    onClick={(e) => handleMarkReviewed(error.id, error.reviewed || false, e)}
                    disabled={updatingReviewed === error.id}
                    className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors mt-0.5 ${
                      error.reviewed
                        ? 'bg-green-500 border-green-500'
                        : 'border-gray-300 hover:border-green-500'
                    } ${updatingReviewed === error.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    title={error.reviewed ? 'Markeren als onbekeken' : 'Markeren als bekeken'}
                  >
                    {error.reviewed && <Check className="w-3 h-3 text-white" />}
                  </button>
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-gray-900">{error.recipient_email}</span>
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">
                        {error.email_type}
                      </span>
                      {error.response_status_code && (
                        <span className="px-2 py-0.5 text-xs font-mono rounded bg-gray-100 text-gray-700">
                          HTTP {error.response_status_code}
                        </span>
                      )}
                    </div>

                    <div className="text-sm text-gray-900 mb-1">
                      {error.subject || error.campaign_subject || 'Geen onderwerp'}
                    </div>

                    <div className="flex items-center gap-3 text-xs text-gray-600 mb-2">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatTimestamp(error.created_at)}</span>
                      </div>
                      {error.user_email && (
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span>{error.user_email}</span>
                        </div>
                      )}
                    </div>

                    {error.error_message && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm">
                        <span className="font-medium text-red-900">Fout: </span>
                        <span className="text-red-800">{error.error_message}</span>
                      </div>
                    )}

                    {error.reviewed && error.reviewed_at && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                        <Check className="w-3 h-3 text-green-600" />
                        <span>Bekeken op {formatTimestamp(error.reviewed_at)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <button className="ml-4 p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0">
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Technische Details</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <dt className="text-gray-600">Email ID:</dt>
                      <dd className="text-gray-900 font-mono text-xs break-all">{error.id}</dd>

                      {error.sendgrid_message_id && (
                        <>
                          <dt className="text-gray-600">SendGrid Message ID:</dt>
                          <dd className="text-gray-900 font-mono text-xs break-all">{error.sendgrid_message_id}</dd>
                        </>
                      )}

                      {error.campaign_id && (
                        <>
                          <dt className="text-gray-600">Campaign ID:</dt>
                          <dd className="text-gray-900 font-mono text-xs break-all">{error.campaign_id}</dd>
                        </>
                      )}

                      {error.user_id && (
                        <>
                          <dt className="text-gray-600">User ID:</dt>
                          <dd className="text-gray-900 font-mono text-xs break-all">{error.user_id}</dd>
                        </>
                      )}

                      <dt className="text-gray-600">Email Type:</dt>
                      <dd className="text-gray-900">{error.email_type}</dd>

                      {error.response_status_code && (
                        <>
                          <dt className="text-gray-600">HTTP Status Code:</dt>
                          <dd className="text-gray-900">{error.response_status_code}</dd>
                        </>
                      )}
                    </dl>
                  </div>

                  {error.request_payload && (
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Request Payload</h5>
                      <div className="bg-white border border-gray-200 rounded p-3 overflow-x-auto">
                        <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-all">
                          {JSON.stringify(error.request_payload, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {error.response_body && (
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Response Body</h5>
                      <div className="bg-white border border-gray-200 rounded p-3 overflow-x-auto">
                        <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-all">
                          {JSON.stringify(error.response_body, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {hasMore && (
        <div className="text-center pt-4">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Laden...' : 'Meer laden'}
          </button>
        </div>
      )}
    </div>
  );
}
